import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import excelJs from "exceljs";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import ResearchPeriodExtensionModal from "../../../ResearchPeriodExtensionModal";
import Label from "../../../Label";
import Page from "../../../Page";

import {
  getResearchExtension,
  confirmResearchExtensionAdmin,
} from "../../../../core/service/researchExtension.service";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  Card,
  Container,
  Grid,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "name", label: "Name", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
  { id: "faculty", label: "Faculty", alignRight: false },
  { id: "time", label: "Time", alignRight: false },
  { id: "date", label: "Date", alignRight: false },
  { id: "program", label: "Program", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_PHD3Y = "phd_3y";
const ROLE_PHD5Y = "phd_5y";
const ROLE_POSTDOC = "post_doc";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

ExtensionAdminTable.propTypes = {
  listExtension: PropTypes.array,
};

export default function ExtensionAdminTable({ listExtension }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [researchExtension, setResearchExtension] = useState({});

  const [openNamePostDoc, setOpenNamePostDoc] = useState(false);
  const [openNamePhD, setOpenNamePhD] = useState(false);

  const getResearchExtensionById = async (re_id) => {
    let data = await getResearchExtension(re_id);
    setResearchExtension(data);
  };
  const openName = (role, re_id) => {
    getResearchExtensionById(re_id);
    if (role === ROLE_POSTDOC) {
      setOpenNamePostDoc(true);
    } else {
      setOpenNamePhD(true);
    }
  };

  const closeName = () => {
    setResearchExtension({});
    setOpenNamePhD(false);
    setOpenNamePostDoc(false);
  };

  const adminConfirm = async (row, confirm) => {
    const { id } = row;
    await confirmResearchExtensionAdmin(id, confirm);
    closeName();
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - listExtension.length) : 0;

  const filteredUsers = applySortFilter(
    listExtension,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);

    ws.columns = [
      {
        header: "ID",
        key: "id",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Status",
        key: "status",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Faculty",
        key: "faculty",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Time",
        key: "time",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Date",
        key: "created_date",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Program",
        key: "role_nm",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          val.student_id,
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.mentor_approve === 0 && val.admin_approve === 0
            ? "Pending"
            : val.mentor_approve === 1
            ? "Approve"
            : val.admin_approve === 1
            ? "Approve"
            : "Disapprove",
          val.faculty,
          val.how_long_extension_period,
          val.created_date,
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `RESEARCH_PERIOD_EXTENSION_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };

  return (
    <Page title="Research Period Extension Admin">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Request for Research Period Extension
          </Typography>
          <Link href={`${config.host_api}export/research-period-extension`}>
            {" "}
            <Button variant="contained">Export excel</Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={listExtension.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const {
                      id,
                      student_id,
                      name_en,
                      middle_en,
                      surname_en,
                      mentor_approve,
                      admin_approve,
                      faculty,
                      how_long_extension_period,
                      role_nm,
                      created_date,
                    } = row;

                    return (
                      <TableRow tabIndex={-1} hover key={id}>
                        <TableCell />

                        <TableCell align="left">{student_id}</TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>
                              {`${name_en} ${middle_en} ${surname_en}` || ""}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              mentor_approve === 0 && admin_approve === 0
                                ? "warning"
                                : mentor_approve === 1
                                ? "success"
                                : admin_approve === 1
                                ? "success"
                                : "error"
                            }
                          >
                            {mentor_approve === 0 && admin_approve === 0
                              ? "Pending"
                              : mentor_approve === 1
                              ? "Approve"
                              : admin_approve === 1
                              ? "Approve"
                              : "Disapprove"}
                          </Label>
                        </TableCell>

                        <TableCell align="left">{faculty}</TableCell>

                        <TableCell align="left">
                          {how_long_extension_period}
                        </TableCell>

                        <TableCell
                          sx={{ textTransform: "capitalize" }}
                          align="left"
                        >
                          {created_date}
                        </TableCell>

                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>

                        <TableCell align="left">
                          <IconButton onClick={() => openName(role_nm, id)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>

                        <Dialog
                          open={openNamePhD}
                          onClose={closeName}
                          fullWidth={true}
                          maxWidth={"lg"}
                        >
                          <Stack alignItems="center">
                            <DialogTitle>Approve</DialogTitle>
                          </Stack>
                          <DialogContent>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        ID:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.student_id}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Name:
                                      </Typography>
                                      <Typography>
                                        {`${researchExtension?.name_en} ${researchExtension?.middle_en} ${researchExtension?.surname_en}`}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Program:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>

                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Request Date:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.created_date}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Mentor:
                                      </Typography>
                                      <Typography>
                                        {`${researchExtension?.mentor_name_en} ${researchExtension?.mentor_middle_en} ${researchExtension?.mentor_surname_en}`}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <ResearchPeriodExtensionModal
                              detail={researchExtension}
                            />
                          </DialogContent>
                          {researchExtension.mentor_approve === 0
                            ? researchExtension.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 2)
                                    }
                                  >
                                    Disapprove
                                  </Button>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 1)
                                    }
                                  >
                                    Approve
                                  </Button>
                                </DialogActions>
                              )
                            : researchExtension.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 3)
                                    }
                                  >
                                    Confirm
                                  </Button>
                                </DialogActions>
                              )}
                        </Dialog>

                        <Dialog
                          open={openNamePostDoc}
                          onClose={closeName}
                          fullWidth={true}
                          maxWidth={"lg"}
                        >
                          <Stack alignItems="center">
                            <DialogTitle>Approve</DialogTitle>
                          </Stack>
                          <DialogContent>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        ID:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Name:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Program:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>

                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Request Date:
                                      </Typography>
                                      <Typography>
                                        {researchExtension?.created_date}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Mentor:
                                      </Typography>
                                      <Typography>
                                        {`${researchExtension?.mentor_name_en} ${researchExtension?.mentor_middle_en} ${researchExtension?.mentor_surname_en}`}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <ResearchPeriodExtensionModal
                              detail={researchExtension}
                            />
                          </DialogContent>
                          {researchExtension.mentor_approve === 0
                            ? researchExtension.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 2)
                                    }
                                  >
                                    Disapprove
                                  </Button>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 1)
                                    }
                                  >
                                    Approve
                                  </Button>
                                </DialogActions>
                              )
                            : researchExtension.admin_approve === 0 && (
                                <DialogActions>
                                  <Button
                                    onClick={() =>
                                      adminConfirm(researchExtension, 3)
                                    }
                                  >
                                    Confirm
                                  </Button>
                                </DialogActions>
                              )}
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={listExtension.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
